import { endOfDay, parseISO, startOfDay } from 'date-fns';
import api from 'src/utils/ajax';

import { populateRelationshipFromRequest } from '../../../utils/populateRelationship';
import { fetchUsers } from '../../user/user';
import { fetchTasks } from '../task/task';

import {
  ClockEntryAggregates,
  denormalizeClockEntry,
  normalizeClockEntry,
  normalizeClockEntryAggregates,
} from './normalizers';

import type {
  ClockEntry,
  ClockEntryFilters,
  ClockEntryWithTask,
  ClockEntryWithUserAndTask,
} from './_types';
import type { User } from '../../user/_types';
import type { Identifier } from 'types/utilityTypes';

export const createClockEntry = async (
  entry: Partial<ClockEntry>
): Promise<ClockEntry> => {
  const response = await api.post(
    `/api/v4/clock/log_entries`,
    denormalizeClockEntry(entry)
  );
  return normalizeClockEntry(response.data);
};

export const updateClockEntry = async (
  entry: Partial<ClockEntry>
): Promise<ClockEntry> => {
  const response = await api.put(
    `/api/v4/clock/log_entries/${entry.id}`,
    denormalizeClockEntry(entry)
  );
  return normalizeClockEntry(response.data);
};

export const deleteClockEntry = async (
  clockEntry: Partial<ClockEntry>
): Promise<ClockEntry> => {
  const response = await api.delete(
    `/api/v4/clock/log_entries/${clockEntry.id}`
  );
  return normalizeClockEntry(response.data);
};

export const fetchLatestEntries = async (): Promise<ClockEntryWithTask[]> => {
  const response = await api.get(`/api/v4/clock/log_entries/latest`);
  const entries = response.data?.map((entry: any) => {
    return normalizeClockEntry(entry);
  });

  if (!entries) {
    return [];
  }

  return addTaskToEntries(entries);
};

interface FetchEntriesOptions {
  page?: number;
  perPage?: number;
  sort?: string;
  sortDir?: string;
  filters?: Partial<ClockEntryFilters>;
  withUserAndTask?: boolean;
}

interface FetchEntriesResponse {
  entries: ClockEntryWithUserAndTask[];
  aggregates: ClockEntryAggregates;
  total: number;
}

export const fetchEntries = async ({
  page = 1,
  perPage = 20,
  sort,
  sortDir,
  filters = {},
  withUserAndTask = true,
}: FetchEntriesOptions): Promise<FetchEntriesResponse> => {
  const response = await api.get(`/api/v4/clock/log_entries`, {
    params: {
      page: page,
      per: perPage,
      q: {
        s: `${sort} ${sortDir}`,
        user_id_eq: filters.trainers?.join(','),
        task_id_eq: filters.tasks?.join(','),
        start_date_gteq:
          typeof filters.startDate === 'string'
            ? startOfDay(parseISO(filters.startDate))
            : filters.startDate,
        end_date_lteq:
          typeof filters.endDate === 'string'
            ? endOfDay(parseISO(filters.endDate))
            : filters.endDate,
      },
    },
  });

  const entries = response.data.log_entry?.map((entry: any) => {
    return normalizeClockEntry(entry);
  });

  if (!entries) {
    return {
      entries: [],
      aggregates: { totalHours: 0, totalPay: 0, averagePayrate: 0 },
      total: 0,
    };
  }

  return {
    entries: withUserAndTask ? await addUserAndTaskToEntries(entries) : entries,
    aggregates: normalizeClockEntryAggregates(response.data.meta.aggregates),
    total: response.data.meta.total,
  };
};

async function addUserAndTaskToEntries(
  entries: ClockEntry[]
): Promise<ClockEntryWithUserAndTask[]> {
  const entriesWithUser = await populateRelationshipFromRequest({
    list: entries,
    idKey: 'userId',
    newKey: 'user',
    func: (ids: Identifier[]) => fetchUsers({ ids }),
  });

  const entriesWithUserAndTask = await populateRelationshipFromRequest({
    list: entriesWithUser,
    idKey: 'taskId',
    newKey: 'task',
    func: (ids: Identifier[]) => fetchTasks({ ids }),
  });

  return entriesWithUserAndTask;
}

async function addTaskToEntries(
  entries: ClockEntry[]
): Promise<ClockEntryWithTask[]> {
  const entriesWithTask = await populateRelationshipFromRequest({
    list: entries,
    idKey: 'taskId',
    newKey: 'task',
    func: (ids: Identifier[]) => fetchTasks({ ids }),
  });

  return entriesWithTask;
}

interface ClockInOptions {
  taskId: Identifier;
  userId: Identifier;
  notes: string;
}

export async function clockIn({
  taskId,
  userId,
  notes,
}: ClockInOptions): Promise<ClockEntry> {
  const response = await api.post(`/api/v4/clock/log_entries/clock_in`, {
    task_id: taskId,
    user_id: userId,
    notes,
  });
  return normalizeClockEntry(response.data);
}

export async function clockOut(user: User, notes: string): Promise<ClockEntry> {
  const response = await api.put(`/api/v4/clock/log_entries/clock_out`, {
    user_id: user.id,
    notes,
  });
  return normalizeClockEntry(response.data);
}

export async function fetchLatestForUser(
  user: User
): Promise<ClockEntryWithTask | null> {
  const response = await api.get(`/api/v4/clock/log_entries/latest_for_user`, {
    params: {
      user_id: user.id,
    },
  });
  const { data } = response;

  if (!data) return null;

  const clock = normalizeClockEntry(data);
  const clockWithTask = await populateRelationshipFromRequest({
    list: [clock],
    idKey: 'taskId',
    newKey: 'task',
    func: (ids: Identifier[]) => fetchTasks({ ids }),
  });
  return clockWithTask[0];
}
